<template>
  <!-- Page Header -->
  <PageHeader 
    title="Mails d'invitation d'acheteurs" 
    subtitle="Envoyez des emails promotionnels à vos acheteurs"
    class="mb-0" 
  />
  
  <div class="w-full px-2 sm:px-6 py-4">
    <!-- Formulaire d'envoi d'email -->
    <div class="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Expéditeur (fixe) -->
        <div class="flex items-center">
          <label class="w-1/3 text-sm font-medium text-gray-700">Expéditeur</label>
          <div class="w-2/3">
            <input
              type="email"
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              value="service@efashion-paris.com"
              readonly
            />
          </div>
        </div>

        <!-- Destinataire (CSV) -->
        <div class="flex items-center">
          <label class="w-1/3 text-sm font-medium text-gray-700">Destinataire (CSV)</label>
          <div class="w-2/3 flex items-center gap-3">
            <input
              ref="fileInput"
              type="file"
              accept=".csv"
              @change="handleFileUpload"
              class="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <ActionButton
              label="Demo CSV"
              variant="secondary"
              @click="downloadTemplate"
            />
          </div>
        </div>

        <!-- Objet (généré automatiquement) -->
        <div class="flex items-center">
          <label class="w-1/3 text-sm font-medium text-gray-700">Objet</label>
          <div class="w-2/3">
            <input
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              value="Commander maintenant en ligne !"
              readonly
            />
          </div>
        </div>

            <!-- Aperçu du template -->
            <div class="flex items-start">
              <label class="w-1/3 text-sm font-medium text-gray-700 mt-2">Aperçu</label>
              <div class="w-2/3">
                <div class="flex gap-3">
                  <button
                    @click="openPreview('fr')"
                    :disabled="isLoadingPreview"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg v-if="isLoadingPreview" class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ isLoadingPreview ? 'Chargement...' : 'Aperçu FR' }}
                  </button>
                  <button
                    @click="openPreview('en')"
                    :disabled="isLoadingPreview"
                    class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg v-if="isLoadingPreview" class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ isLoadingPreview ? 'Chargement...' : 'Aperçu EN' }}
                  </button>
                </div>
              </div>
            </div>

        <!-- Bouton d'envoi -->
        <div class="flex justify-end pt-4">
          <ActionButton
            :label="isSending ? 'Envoi en cours...' : 'Envoyer'"
            variant="primary"
            type="submit"
            :loading="isSending"
            :disabled="!emailForm.recipients || isSending"
          />
        </div>
      </form>
    </div>


  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { PageHeader, ActionButton } from '@/components'
import { useNotification } from '@/composables/useNotification'
import { sendInvitations, getEmailPreview } from '@/api/invitations'
import { useAuthStore } from '@/stores/auth'
import type { EmailForm, InvitationResult } from '@/types'

// UI State
const isSending = ref(false)
const isLoadingPreview = ref(false)
const fileInput = ref<HTMLInputElement>()

// Notifications
const { success, error } = useNotification()

// Auth store
const authStore = useAuthStore()

// Form data
const emailForm = reactive<EmailForm>({
  recipients: null
})
const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    emailForm.recipients = target.files[0]
  }
}

const readFileContent = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      resolve(content)
    }
    reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'))
    reader.readAsText(file, 'UTF-8')
  })
}

const handleSubmit = async () => {
  try {
    isSending.value = true
    
    // Vérifier l'authentification
    if (!authStore.isAuthenticated) {
      error('Vous devez être connecté pour envoyer des emails')
      return
    }
    
    // Vérifier qu'un fichier CSV est sélectionné
    if (!emailForm.recipients) {
      error('Veuillez sélectionner un fichier CSV')
      return
    }
    
    // Lire le contenu du fichier CSV
    const csvContent = await readFileContent(emailForm.recipients)
    
    // Envoyer les invitations via l'API
    const result: InvitationResult = await sendInvitations(csvContent)
    
    // Afficher le résultat
    if (result.success) {
      success(`${result.successful} emails d'invitation envoyés avec succès !`)
    } else {
      error(`${result.failed} erreurs sur ${result.totalProcessed} emails`)
    }
    
    // Reset form
    emailForm.recipients = null
    if (fileInput.value) {
      fileInput.value.value = ''
    }
    
  } catch (err) {
    error('Erreur lors de l\'envoi des emails d\'invitation')
  } finally {
    isSending.value = false
  }
}

const downloadTemplate = () => {
  try {
    const a = document.createElement('a')
    a.href = '/templateEmail.csv'
    a.download = 'templateEmail.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    
    success('Template CSV téléchargé avec succès !')
  } catch (err) {
    error('Erreur lors du téléchargement du template CSV')
  }
}

const openPreview = async (language: string) => {
  try {
    isLoadingPreview.value = true
    
    if (!authStore.isAuthenticated) {
      error('Vous devez être connecté pour voir l\'aperçu')
      return
    }
    
    const htmlContent = await getEmailPreview(language)
    const previewWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes')
    
    if (!previewWindow) {
      error('Impossible d\'ouvrir l\'aperçu. Vérifiez que les pop-ups sont autorisés.')
      return
    }
    
    previewWindow.document.write(`
      <!DOCTYPE html>
      <html lang="${language}">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Aperçu Email - Efashion Paris</title>
        <style>
          body { margin: 0; padding: 20px; background-color: #f5f5f5; font-family: Arial, sans-serif; }
          .preview-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); overflow: hidden; }
        </style>
      </head>
      <body>
        <div class="preview-container">
          ${htmlContent}
        </div>
      </body>
      </html>
    `)
    previewWindow.document.close()
    
  } catch (err) {
    error('Erreur lors de l\'ouverture de l\'aperçu')
  } finally {
    isLoadingPreview.value = false
  }
}
</script>
